import { AbortedApiClientError, ApiClientError, unwrap } from "./errors";
import { RequestBuilder } from "./request-builder";
import { type Result, err, ok } from "./result";
import type { Stream } from "./stream";
import { iterateEventSource } from "./stream/iterate-event-source";
import type { ExtractRouteParams, Middleware, Options } from "./types";
import { ContentType, HttpMethod, isErrorMiddleware } from "./types";

export class HttpClient {
	private _middlewares = new Set<Middleware>();
	private _prefix: string[] = [];

	constructor(private _baseUrl: string) {}

	public prefix(prefix: string): this {
		this._prefix = [...this._prefix, prefix];

		return this;
	}

	public version(version: string): this {
		this._prefix = [...this._prefix, version];

		return this;
	}

	public use(middleware: Middleware): this {
		this._middlewares.add(middleware);

		return this;
	}

	// NOTE: (bwilytsch): We only support SSE at the moment,
	// so there is no need to extend it.
	// We want to have a reconnect feature in here to keep the stream alive
	public async stream<
		TResponse = unknown,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(
		path: string,
		options: Options<TParams> = {},
	): Promise<Result<Stream<TResponse>>> {
		try {
			const { params = {}, query = {} } = options;
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const request = await new RequestBuilder(url.toString(), HttpMethod.Get)
				.use(this._middlewares)
				.params(params)
				.searchParams(query)
				.build();

			const stream = iterateEventSource<TResponse>(request);

			return ok(stream);
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	// await client.get<T>("/hello", { foo: "bar" })
	// await client.get<T>("/hello/:id", { foo: "bar", id: "123" })
	public async get<
		TResponse = unknown,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(
		path: TPath,
		options: Options<TParams> = {},
	): Promise<Result<TResponse, ApiClientError>> {
		try {
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const { params = {}, query = {} } = options;

			const request = await new RequestBuilder(url.toString(), HttpMethod.Get)
				.use(this._middlewares)
				.searchParams(query)
				.params(params)
				.build();

			const response = await fetch(request, { signal: options.signal });

			if (options.signal?.aborted) {
				throw new AbortedApiClientError("GET request aborted");
			}

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ detail: response.statusText }));
				const error = new ApiClientError(
					errorData.detail || response.statusText,
					response.status,
				);
				throw error;
			}

			if (response.status === 204) {
				return ok(null as TResponse);
			}

			const contentType = response.headers.get("Content-Type");

			if (contentType?.includes(ContentType.Html)) {
				const html = (await response.text()) as TResponse;
				return ok(html);
			}

			if (contentType?.includes(ContentType.Json)) {
				const json = (await response.json()) as TResponse;
				return ok(json);
			}

			throw new Error(`Unknown content type ${contentType}`);
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	// await client.post<T>("/hello").json({foo: "bar"})
	// await client.post<T>("/hello", { data: object | File | FormData, params: {} }, )
	public async post<
		TResponse,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(path: TPath, options: Options<TParams> = {}): Promise<Result<TResponse>> {
		try {
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const { data, params = {}, query = {} } = options;

			const request = await new RequestBuilder(url.toString(), HttpMethod.Post)
				.use(this._middlewares)
				.searchParams(query)
				.params(params)
				.data(data)
				.build();

			const response = await fetch(request);

			if (options.signal?.aborted) {
				throw new AbortedApiClientError("POST request aborted");
			}

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ detail: response.statusText }));
				const error = new ApiClientError(
					errorData.detail || response.statusText,
					response.status,
				);
				throw error;
			}

			if (response.status === 204) {
				return ok(null as TResponse);
			}

			const contentType = response.headers.get("Content-Type");

			if (contentType?.includes(ContentType.Json)) {
				const json = (await response.json()) as TResponse;
				return ok(json);
			}

			if (contentType?.includes(ContentType.Html)) {
				const html = (await response.text()) as TResponse;
				return ok(html);
			}

			throw new Error(`Unknown content type ${contentType}`);
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	public async delete<
		TResponse = unknown,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(
		path: TPath,
		options: Options<TParams> = {},
	): Promise<Result<TResponse | undefined>> {
		try {
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const { data, params = {} } = options;

			const request = await new RequestBuilder(
				url.toString(),
				HttpMethod.Delete,
			)
				.use(this._middlewares)
				.params(params)
				.data(data)
				.build();

			const response = await fetch(request);

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ detail: response.statusText }));
				const error = new ApiClientError(
					errorData.detail || response.statusText,
					response.status,
				);
				throw error;
			}

			if (response.status === 204) {
				return ok(undefined);
			}

			if (response.headers.get("Content-Type")?.includes(ContentType.Json)) {
				const json = (await response.json()) as TResponse;
				return ok(json);
			}

			return ok(undefined);
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	public async patch<
		TResponse,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(path: TPath, options: Options<TParams> = {}): Promise<Result<TResponse>> {
		try {
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const { data, params = {} } = options;

			const request = await new RequestBuilder(url.toString(), HttpMethod.Patch)
				.use(this._middlewares)
				.params(params)
				.data(data)
				.build();

			const response = await fetch(request);

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ detail: response.statusText }));
				const error = new ApiClientError(
					errorData.detail || response.statusText,
					response.status,
				);
				throw error;
			}

			if (response.status === 204) {
				return ok(null as TResponse);
			}

			if (response.headers.get("Content-Type")?.includes(ContentType.Json)) {
				const json = (await response.json()) as TResponse;
				return ok(json);
			}

			throw new Error("Unknown content type");
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	public async put<
		TResponse,
		TPath extends string = string,
		TParams extends ExtractRouteParams<TPath> = ExtractRouteParams<TPath>,
	>(path: TPath, options: Options<TParams> = {}): Promise<Result<TResponse>> {
		try {
			const url = new URL(this._baseUrl + this._prefix.join("/") + path);
			const { data, params = {} } = options;

			const request = await new RequestBuilder(url.toString(), HttpMethod.Put)
				.use(this._middlewares)
				.params(params)
				.data(data)
				.build();

			const response = await fetch(request);

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ detail: response.statusText }));
				const error = new ApiClientError(
					errorData.detail || response.statusText,
					response.status,
				);
				throw error;
			}

			if (response.status === 204) {
				return ok(null as TResponse);
			}

			if (response.headers.get("Content-Type")?.includes(ContentType.Json)) {
				const json = (await response.json()) as TResponse;
				return ok(json);
			}

			throw new Error("Unknown content type");
		} catch (error) {
			return err(await this._processError(error));
		}
	}

	private _processError = async (
		errorCandidate: unknown,
	): Promise<ApiClientError> => {
		const error = unwrap(errorCandidate);

		const errorMiddleware = Array.from(this._middlewares).filter(
			isErrorMiddleware,
		);

		try {
			await Promise.allSettled(errorMiddleware.map((m) => m.onError(error)));
		} catch (e) {
			console.log("error middleware error", e);
		}

		return error;
	};
}
